import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';

/* ═══════════════════════════════════════════════════════════════
   useScrollReveal — standard Seikural scroll animation hook
   ═══════════════════════════════════════════════════════════════ */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll(
      '.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt'
    );
    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -60px 0px',
      }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

/* ═══════════════════════════════════════════════════════════════
   DATA CONSTANTS
   ═══════════════════════════════════════════════════════════════ */

const SERVICE_OPTIONS = [
  'Web & Application Development',
  'Business Software Solutions',
  'UI/UX & Product Design',
  'Cloud & DevOps',
  'Digital Marketing',
  'ERP Solution',
  'HRM Solution',
  'CRM Solution',
  'LMS Solution',
  'Custom Business Application',
  'Other / Not Sure',
];

const STAGE_OPTIONS = [
  'I have an idea',
  'Planning / Requirements',
  'Design ready',
  'Development required',
  'Existing product improvement',
  'Maintenance / Support',
  'Not sure',
];

const PROCESS_STEPS = [
  {
    num: '01',
    stepLabel: 'STEP 01',
    title: 'Initial Review',
    desc: 'Our engineering leads carefully evaluate your requirement context within 24 business hours.',
    badgeBg: 'tw-bg-sipl-navy',
    accentGradient: 'tw-from-blue-950 tw-to-blue-800',
    delay: '0.06s',
  },
  {
    num: '02',
    stepLabel: 'STEP 02',
    title: 'Discovery Call',
    desc: 'We schedule a focused 15–30 minute conversation to clarify technical goals, scope, and objectives.',
    badgeBg: 'tw-bg-sipl-teal',
    accentGradient: 'tw-from-teal-700 tw-to-teal-500',
    delay: '0.16s',
  },
  {
    num: '03',
    stepLabel: 'STEP 03',
    title: 'Proposal & Roadmap',
    desc: 'You receive an itemized proposal featuring architectural strategy, deliverables, timelines, and budget.',
    badgeBg: 'tw-bg-sipl-green',
    accentGradient: 'tw-from-green-600 tw-to-emerald-500',
    delay: '0.26s',
  },
  {
    num: '04',
    stepLabel: 'STEP 04',
    title: 'Project Kickoff',
    desc: 'Once approved, we align dedicated designers and developers to start building without delay.',
    badgeBg: 'tw-bg-sipl-orange',
    accentGradient: 'tw-from-orange-500 tw-to-amber-500',
    delay: '0.36s',
  },
];

const OTHER_PATHS = [
  {
    icon: (
      <svg className="tw-w-6 tw-h-6 tw-text-sipl-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: 'General & Project Enquiries',
    desc: 'For initial requirements, business software, and solution consultations.',
    contactLabel: 'info@seikural.com',
    link: 'mailto:info@seikural.com',
    delay: '0.08s',
  },
  {
    icon: (
      <svg className="tw-w-6 tw-h-6 tw-text-sipl-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Business Consultation',
    desc: 'Contact us for custom engineering specs and enterprise proposals.',
    contactLabel: 'enquiry@seikural.com',
    link: 'mailto:enquiry@seikural.com',
    delay: '0.20s',
  },
  {
    icon: (
      <svg className="tw-w-6 tw-h-6 tw-text-sipl-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Office Address',
    desc: 'No. 10, Shankar Layout, GN Mills, Coimbatore - 641029',
    contactLabel: '9751836710',
    link: 'tel:+919751836710',
    delay: '0.32s',
  },
];

/* ═══════════════════════════════════════════════════════════════
   MAIN CONTACT COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const Contact = () => {
  useScrollReveal();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    projectStage: '',
    requirement: '',
    privacyConsent: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  /* ── Form Field Change Handler ── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error on user edit
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  /* ── Form Validation ── */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Work Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service or interest area.';
    }

    if (!formData.requirement.trim()) {
      newErrors.requirement = 'Please tell us about your requirement.';
    } else if (formData.requirement.trim().length < 20) {
      newErrors.requirement = 'Requirement description must be at least 20 characters.';
    } else if (formData.requirement.trim().length > 2000) {
      newErrors.requirement = 'Requirement description must not exceed 2000 characters.';
    }

    if (!formData.privacyConsent) {
      newErrors.privacyConsent = 'You must acknowledge the privacy statement to proceed.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ── Form Submit Handler ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setSubmitError('The enquiry email service is not configured. Please contact us directly at info@seikural.com.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.fullName.trim(),
          reply_to: formData.email.trim(),
          phone: formData.phone.trim() || 'Not provided',
          company: formData.company.trim() || 'Not provided',
          service: formData.service,
          project_stage: formData.projectStage || 'Not provided',
          requirement: formData.requirement.trim(),
        },
        { publicKey }
      );
      setIsSubmitted(true);
      window.scrollTo({ top: document.getElementById('contact-section')?.offsetTop - 80 || 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Unable to send contact enquiry:', error);
      setSubmitError('We could not send your enquiry right now. Please try again or email info@seikural.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Reset Form Handler ── */
  const handleReset = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      projectStage: '',
      requirement: '',
      privacyConsent: false,
    });
    setErrors({});
    setSubmitError('');
    setIsSubmitted(false);
  };

  const inputClass = (fieldName) =>
    `form-control py-2 px-3 tw-bg-slate-50 tw-border-slate-300 focus:tw-bg-white focus:tw-border-teal-600 focus:tw-ring-4 focus:tw-ring-teal-500/15 hover:tw-border-slate-400 tw-transition-all tw-duration-300 ${
      errors[fieldName] ? 'is-invalid' : ''
    }`;

  const selectClass = (fieldName) =>
    `form-select py-2 px-3 tw-bg-slate-50 tw-border-slate-300 focus:tw-bg-white focus:tw-border-teal-600 focus:tw-ring-4 focus:tw-ring-teal-500/15 hover:tw-border-slate-400 tw-transition-all tw-duration-300 ${
      errors[fieldName] ? 'is-invalid' : ''
    }`;

  return (
    <div className="tw-bg-white tw-text-slate-800">
      
      {/* ─────────────────────────────────────────────────────────
          1. HERO SECTION (With Subtle Animated Ambient Glowing Orbs)
          ───────────────────────────────────────────────────────── */}
      <section className="py-4 py-lg-5 tw-bg-slate-50 border-bottom tw-border-slate-200 position-relative overflow-hidden">
        
        {/* Animated Background Glowing Ambient Orbs */}
        <div
          className="position-absolute top-0 start-50 translate-middle-x tw-w-96 tw-h-96 tw-bg-teal-400/10 rounded-circle tw-blur-3xl tw-pointer-events-none"
          style={{ animation: 'sipl-orb-drift-1 14s ease-in-out infinite' }}
        />
        <div
          className="position-absolute bottom-0 start-25 tw-w-72 tw-h-72 tw-bg-blue-600/10 rounded-circle tw-blur-3xl tw-pointer-events-none"
          style={{ animation: 'sipl-orb-drift-2 18s ease-in-out infinite' }}
        />

        <div className="container position-relative z-1">
          <div className="row justify-content-center text-center">
            <div className="col-lg-9 col-xl-8 sipl-reveal">
              <span className="d-inline-block px-3 py-1 mb-3 rounded-pill fw-bold text-uppercase tw-bg-teal-50 tw-text-sipl-teal tw-border tw-border-teal-200 shadow-sm" style={{ fontSize: '0.75rem', letterSpacing: '0.12em' }}>
                Contact Us
              </span>
              <h1 className="fw-bold tw-text-transparent tw-bg-clip-text tw-bg-gradient-to-r tw-from-slate-900 tw-via-slate-800 tw-to-teal-800 mb-3" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '1.2' }}>
                Let's Talk About What You Want to Build.
              </h1>
              <p className="tw-text-slate-600 mb-3 fs-5" style={{ lineHeight: '1.6', maxWidth: '700px', margin: '0 auto' }}>
                Have a project, business challenge, or digital idea in mind? Tell us what you're trying to achieve and we'll help you identify a practical way forward.
              </p>
              <p className="tw-text-slate-500 mb-0" style={{ fontSize: '0.9rem' }}>
                Whether you need a new application, business software, product design, cloud support, or digital growth services, start by telling us about your requirement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          2. MAIN CONTACT SECTION (Contact Form + Contact Info)
          ───────────────────────────────────────────────────────── */}
      <section id="contact-section" className="py-4 py-lg-5 tw-bg-white">
        <div className="container py-lg-2">
          <div className="row g-4 g-lg-5 align-items-start">
            
            {/* LEFT COLUMN: Contact Details (Responsive: col-lg-6 col-xl-5) */}
            <div className="col-lg-6 col-xl-5 order-2 order-lg-1">
              <div className="pe-xl-3 py-1">
                
                {/* Eyebrow & Headline (Reveals First) */}
                <div className="mb-4 sipl-reveal" style={{ transitionDelay: '0s' }}>
                  <span className="d-inline-block fw-bold text-uppercase tw-text-sipl-teal mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.12em' }}>
                    Start a Conversation
                  </span>
                  <h2 className="fw-bold tw-text-slate-900 mb-3" style={{ fontSize: 'clamp(1.75rem, 2.3vw, 2.25rem)', lineHeight: '1.25' }}>
                    Tell us what you're working on.
                  </h2>
                  <p className="tw-text-slate-600 mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.65' }}>
                    You don't need to have every detail figured out. Share your requirement, challenge, or idea and give us enough context to understand what you need.
                  </p>
                </div>

                {/* Info Cards (Revealed Slightly Faster One by One) */}
                <div className="d-flex flex-column gap-3 mb-4">
                  
                  {/* General Enquiries (Reveals Step 1: 0.08s) */}
                  <div
                    className="group p-3 rounded-3 tw-bg-slate-50 border tw-border-slate-200 d-flex gap-3 align-items-start shadow-sm hover:shadow-md hover:-tw-translate-y-1 hover:tw-border-teal-400/60 tw-transition-all tw-duration-500 sipl-reveal-btt"
                    style={{ transitionDelay: '0.08s' }}
                  >
                    <div className="p-2 rounded-2 tw-bg-blue-50 tw-text-sipl-navy shrink-0 group-hover:tw-scale-110 group-hover:tw-bg-blue-100 tw-transition-all tw-duration-500">
                      <svg className="tw-w-5 tw-h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="fw-semibold tw-text-slate-900 mb-1" style={{ fontSize: '0.95rem' }}>
                        General & Project Enquiries
                      </h3>
                      <a href="mailto:info@seikural.com" className="fw-medium tw-text-sipl-navy hover:tw-text-sipl-orange text-decoration-none d-block mb-1 tw-transition-colors tw-duration-300" style={{ fontSize: '0.9rem' }}>
                        info@seikural.com
                      </a>
                      <p className="tw-text-slate-500 mb-0" style={{ fontSize: '0.825rem' }}>
                        For initial requirements, business software, and solution consultations.
                      </p>
                    </div>
                  </div>

                  {/* Business & Direct Contact (Reveals Step 2: 0.20s) */}
                  <div
                    className="group p-3 rounded-3 tw-bg-slate-50 border tw-border-slate-200 d-flex gap-3 align-items-start shadow-sm hover:shadow-md hover:-tw-translate-y-1 hover:tw-border-teal-400/60 tw-transition-all tw-duration-500 sipl-reveal-btt"
                    style={{ transitionDelay: '0.20s' }}
                  >
                    <div className="p-2 rounded-2 tw-bg-teal-50 tw-text-sipl-teal shrink-0 group-hover:tw-scale-110 group-hover:tw-bg-teal-100 tw-transition-all tw-duration-500">
                      <svg className="tw-w-5 tw-h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="fw-semibold tw-text-slate-900 mb-1" style={{ fontSize: '0.95rem' }}>
                        Business Consultation
                      </h3>
                      <a href="mailto:enquiry@seikural.com" className="fw-medium tw-text-sipl-navy hover:tw-text-sipl-orange text-decoration-none d-block mb-1 tw-transition-colors tw-duration-300" style={{ fontSize: '0.9rem' }}>
                        enquiry@seikural.com
                      </a>
                      <p className="tw-text-slate-500 mb-0" style={{ fontSize: '0.825rem' }}>
                        Direct line for custom engineering specs and enterprise proposals.
                      </p>
                    </div>
                  </div>

                  {/* Response Expectation & SLA (Reveals Step 3: 0.32s) */}
                  <div
                    className="group p-3 rounded-3 tw-bg-slate-50 border tw-border-slate-200 d-flex gap-3 align-items-start shadow-sm hover:shadow-md hover:-tw-translate-y-1 hover:tw-border-teal-400/60 tw-transition-all tw-duration-500 sipl-reveal-btt"
                    style={{ transitionDelay: '0.32s' }}
                  >
                    <div className="p-2 rounded-2 tw-bg-green-50 tw-text-sipl-green shrink-0 group-hover:tw-scale-110 group-hover:tw-bg-green-100 tw-transition-all tw-duration-500">
                      <svg className="tw-w-5 tw-h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h3 className="fw-semibold tw-text-slate-900 mb-0" style={{ fontSize: '0.95rem' }}>
                          Response Expectation
                        </h3>
                        <span className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded-pill tw-bg-green-100/80 tw-text-green-800 fw-semibold" style={{ fontSize: '0.725rem' }}>
                          <span className="tw-w-2 tw-h-2 rounded-circle tw-bg-green-500 tw-animate-pulse" />
                          Live SLA
                        </span>
                      </div>
                      <p className="fw-medium tw-text-slate-700 mb-1" style={{ fontSize: '0.875rem' }}>
                        We typically respond within 24 hours on business days.
                      </p>
                      <p className="tw-text-slate-500 mb-0" style={{ fontSize: '0.825rem' }}>
                        Operating Hours: Mon – Sat: 9:00 AM – 6:00 PM IST
                      </p>
                    </div>
                  </div>

                </div>

                {/* Office Location (Reveals Step 4: 0.44s) */}
                <div className="pt-3 border-top tw-border-slate-200 sipl-reveal-btt" style={{ transitionDelay: '0.44s' }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <svg className="tw-w-4 tw-h-4 tw-text-sipl-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="fw-semibold tw-text-slate-900" style={{ fontSize: '0.9rem' }}>
                      Registered Office & Tech Hub
                    </span>
                  </div>
                  <p className="tw-text-slate-600 mb-0 ps-4" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                    Seikural Innovation Pvt. Ltd.<br />
                    No. 10, Shankar Layout, GN Mills<br />
                    Coimbatore - 641029<br />
                    <a href="tel:+919751836710" className="tw-text-sipl-navy text-decoration-none hover:tw-text-sipl-orange">
                      9751836710
                    </a>
                  </p>
                </div>

              </div>
            </div>

            {/* RIGHT COLUMN: Contact Form (Responsive: col-lg-6 col-xl-7) */}
            <div className="col-lg-6 col-xl-7 order-1 order-lg-2 sipl-reveal" style={{ transitionDelay: '0.12s' }}>
              <div className="p-4 p-md-5 rounded-4 shadow-lg tw-bg-white border tw-border-slate-200 hover:tw-border-teal-300 hover:shadow-xl tw-transition-all tw-duration-500">
                
                {isSubmitted ? (
                  /* Success Feedback Card */
                  <div className="text-center py-4 my-auto sipl-reveal">
                    <div className="tw-w-16 tw-h-16 tw-bg-teal-50 tw-text-sipl-teal rounded-circle d-inline-flex align-items-center justify-content-center mb-3 animate-bounce">
                      <svg className="tw-w-8 tw-h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="fw-bold tw-text-slate-900 mb-2" style={{ fontSize: '1.4rem' }}>
                      Enquiry Received!
                    </h3>
                    <p className="tw-text-slate-600 mb-4" style={{ fontSize: '0.925rem', lineHeight: '1.6', maxWidth: '480px', margin: '0 auto' }}>
                      Thank you, <span className="fw-semibold tw-text-slate-900">{formData.fullName}</span>. We have received your request regarding <span className="fw-semibold tw-text-sipl-navy">{formData.service}</span>. Our technical leads will review your requirement and reply to <span className="fw-semibold tw-text-slate-900">{formData.email}</span> within 24 hours.
                    </p>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="btn fw-semibold px-4 py-2 tw-bg-slate-100 tw-text-slate-800 hover:tw-bg-slate-200 active:tw-scale-98 tw-transition-all tw-duration-300"
                      style={{ borderRadius: '8px' }}
                    >
                      Submit Another Requirement
                    </button>
                  </div>
                ) : (
                  /* Standard Enquiry Form */
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="mb-4">
                      <h3 className="fw-bold tw-text-slate-900 mb-1" style={{ fontSize: '1.3rem' }}>
                        Send Us an Enquiry
                      </h3>
                      <p className="tw-text-slate-500 mb-0" style={{ fontSize: '0.85rem' }}>
                        Fill in the required details below to start your requirement review.
                      </p>
                    </div>

                    <div className="row g-3">
                      
                      {/* Full Name */}
                      <div className="col-md-6">
                        <label htmlFor="fullName" className="form-label fw-semibold tw-text-slate-700" style={{ fontSize: '0.85rem' }}>
                          Full Name <span className="tw-text-red-500">*</span>
                        </label>
                        <input
                          id="fullName"
                          name="fullName"
                          type="text"
                          className={inputClass('fullName')}
                          placeholder="Your name"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                        />
                        {errors.fullName && (
                          <div id="fullName-error" className="invalid-feedback" role="alert">
                            {errors.fullName}
                          </div>
                        )}
                      </div>

                      {/* Work Email */}
                      <div className="col-md-6">
                        <label htmlFor="email" className="form-label fw-semibold tw-text-slate-700" style={{ fontSize: '0.85rem' }}>
                          Work Email <span className="tw-text-red-500">*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          className={inputClass('email')}
                          placeholder="name@company.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          aria-describedby={errors.email ? 'email-error' : undefined}
                        />
                        {errors.email && (
                          <div id="email-error" className="invalid-feedback" role="alert">
                            {errors.email}
                          </div>
                        )}
                      </div>

                      {/* Phone Number (Optional) */}
                      <div className="col-md-6">
                        <label htmlFor="phone" className="form-label fw-semibold tw-text-slate-700" style={{ fontSize: '0.85rem' }}>
                          Phone Number <span className="tw-text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          className={inputClass('phone')}
                          placeholder="+1 (555) 000-0000 or +91..."
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>

                      {/* Company / Organization (Optional) */}
                      <div className="col-md-6">
                        <label htmlFor="company" className="form-label fw-semibold tw-text-slate-700" style={{ fontSize: '0.85rem' }}>
                          Company / Organization <span className="tw-text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <input
                          id="company"
                          name="company"
                          type="text"
                          className={inputClass('company')}
                          placeholder="Your company name"
                          value={formData.company}
                          onChange={handleChange}
                        />
                      </div>

                      {/* I'm Interested In */}
                      <div className="col-md-6">
                        <label htmlFor="service" className="form-label fw-semibold tw-text-slate-700" style={{ fontSize: '0.85rem' }}>
                          I'm Interested In <span className="tw-text-red-500">*</span>
                        </label>
                        <select
                          id="service"
                          name="service"
                          className={selectClass('service')}
                          value={formData.service}
                          onChange={handleChange}
                          required
                          aria-describedby={errors.service ? 'service-error' : undefined}
                        >
                          <option value="" disabled>
                            Select a service
                          </option>
                          {SERVICE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                        {errors.service && (
                          <div id="service-error" className="invalid-feedback" role="alert">
                            {errors.service}
                          </div>
                        )}
                      </div>

                      {/* Project Stage (Optional) */}
                      <div className="col-md-6">
                        <label htmlFor="projectStage" className="form-label fw-semibold tw-text-slate-700" style={{ fontSize: '0.85rem' }}>
                          Project Stage <span className="tw-text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <select
                          id="projectStage"
                          name="projectStage"
                          className={selectClass('projectStage')}
                          value={formData.projectStage}
                          onChange={handleChange}
                        >
                          <option value="">Select project stage (Optional)</option>
                          {STAGE_OPTIONS.map((stg) => (
                            <option key={stg} value={stg}>
                              {stg}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Tell Us About Your Requirement */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label htmlFor="requirement" className="form-label fw-semibold tw-text-slate-700 mb-0" style={{ fontSize: '0.85rem' }}>
                            Tell Us About Your Requirement <span className="tw-text-red-500">*</span>
                          </label>
                          <span className="tw-text-slate-400" style={{ fontSize: '0.75rem' }}>
                            {formData.requirement.length}/2000 chars (min 20)
                          </span>
                        </div>
                        <textarea
                          id="requirement"
                          name="requirement"
                          rows={4}
                          className={inputClass('requirement')}
                          placeholder="Tell us briefly about your project, business challenge, or what you'd like to improve."
                          value={formData.requirement}
                          onChange={handleChange}
                          minLength={20}
                          maxLength={2000}
                          required
                          aria-describedby={errors.requirement ? 'requirement-error' : undefined}
                        />
                        {errors.requirement && (
                          <div id="requirement-error" className="invalid-feedback" role="alert">
                            {errors.requirement}
                          </div>
                        )}
                      </div>

                      {/* Consent & Privacy */}
                      <div className="col-12 mt-3">
                        <div className="form-check">
                          <input
                            id="privacyConsent"
                            name="privacyConsent"
                            type="checkbox"
                            className={`form-check-input ${errors.privacyConsent ? 'is-invalid' : ''}`}
                            checked={formData.privacyConsent}
                            onChange={handleChange}
                            required
                          />
                          <label htmlFor="privacyConsent" className="form-check-label tw-text-slate-600" style={{ fontSize: '0.8rem', lineHeight: '1.5' }}>
                            By submitting this form, you agree that Seikural Innovation may use the information provided to respond to your enquiry. Read our{' '}
                            <Link to="/about" className="tw-text-sipl-navy hover:tw-text-sipl-orange hover:tw-underline fw-medium tw-transition-colors tw-duration-300">
                              Privacy Statement
                            </Link>.
                          </label>
                          {errors.privacyConsent && (
                            <div className="invalid-feedback d-block mt-1" role="alert">
                              {errors.privacyConsent}
                            </div>
                          )}
                        </div>
                      </div>

                      {submitError && (
                        <div className="col-12" role="alert">
                          <div className="alert alert-danger mb-0">{submitError}</div>
                        </div>
                      )}

                      {/* Submit Button */}
                      <div className="col-12 mt-4">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="group btn w-100 fw-bold py-3 tw-text-white shadow-sm tw-bg-gradient-to-r tw-from-slate-900 tw-via-teal-700 tw-to-orange-500 hover:tw-shadow-md active:tw-scale-98 tw-transition-all tw-duration-500 d-flex align-items-center justify-content-center gap-2 border-0"
                          style={{
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                          }}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              Sending Enquiry...
                            </>
                          ) : (
                            <>
                              Send Enquiry
                              <svg className="tw-w-4 tw-h-4 group-hover:tw-translate-x-1.5 tw-transition-transform tw-duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </>
                          )}
                        </button>
                      </div>

                    </div>
                  </form>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          3. "WHAT HAPPENS NEXT?" SECTION
          ───────────────────────────────────────────────────────── */}
      <section className="py-4 py-lg-5 tw-bg-slate-50 border-top border-bottom tw-border-slate-200">
        <div className="container py-lg-2">
          <div className="text-center mb-4 mb-lg-5 sipl-reveal">
            <span className="d-inline-block fw-bold text-uppercase tw-text-sipl-teal mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.12em' }}>
              Transparent Process
            </span>
            <h2 className="fw-bold tw-text-slate-900 mb-2" style={{ fontSize: '1.875rem' }}>
              What Happens Next?
            </h2>
            <p className="tw-text-slate-600 mx-auto mb-0" style={{ maxWidth: '600px', fontSize: '0.95rem' }}>
              A straightforward process from your initial message to kickstarting your engineering project.
            </p>
          </div>

          <div className="row g-4">
            {PROCESS_STEPS.map((step) => (
              <div className="col-md-6 col-lg-3 sipl-reveal-btt" key={step.num} style={{ transitionDelay: step.delay }}>
                <div className="group p-4 rounded-4 tw-bg-white border tw-border-slate-200/90 shadow-sm hover:shadow-lg hover:-tw-translate-y-1.5 hover:tw-border-teal-300/80 tw-transition-all tw-duration-500 h-100 d-flex flex-column position-relative overflow-hidden">
                  
                  {/* Top Gradient Accent Line */}
                  <div className={`position-absolute top-0 start-0 end-0 tw-h-1.5 tw-bg-gradient-to-r ${step.accentGradient}`} />
                  
                  {/* Header Badge Row */}
                  <div className="d-flex align-items-center justify-content-between mb-3 pt-1">
                    <div className={`tw-w-9 tw-h-9 rounded-2 text-white fw-bold d-flex align-items-center justify-content-center ${step.badgeBg} group-hover:tw-scale-110 tw-transition-transform tw-duration-300`} style={{ fontSize: '0.85rem' }}>
                      {step.num}
                    </div>
                    <span className="px-3 py-1 rounded-pill tw-bg-slate-100 tw-text-slate-500 fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}>
                      {step.stepLabel}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="fw-bold tw-text-slate-900 mb-2" style={{ fontSize: '1.05rem' }}>
                    {step.title}
                  </h3>
                  <p className="tw-text-slate-600 mb-0" style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          4. OTHER ENQUIRY PATHS
          ───────────────────────────────────────────────────────── */}
      <section className="py-4 py-lg-5">
        <div className="container py-lg-2">
          <div className="text-center mb-4 mb-lg-5 sipl-reveal">
            <span className="d-inline-block fw-bold text-uppercase tw-text-sipl-teal mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.12em' }}>
              Specific Topics
            </span>
            <h2 className="fw-bold tw-text-slate-900 mb-2" style={{ fontSize: '1.85rem' }}>
              Other Ways to Connect
            </h2>
            <p className="tw-text-slate-600 mx-auto mb-0" style={{ maxWidth: '560px', fontSize: '0.925rem' }}>
              Looking for direct communications regarding specific operational areas?
            </p>
          </div>

          <div className="row g-4 justify-content-center">
            {OTHER_PATHS.map((path) => (
              <div className="col-md-4 sipl-reveal-btt" key={path.title} style={{ transitionDelay: path.delay }}>
                <div className="group p-4 rounded-3 tw-bg-slate-50 border tw-border-slate-200 h-100 text-center d-flex flex-column align-items-center shadow-sm hover:shadow-lg hover:-tw-translate-y-1.5 hover:tw-border-teal-300/80 tw-transition-all tw-duration-500">
                  <div className="p-3 rounded-circle tw-bg-white shadow-sm mb-3 group-hover:tw-scale-110 group-hover:tw-bg-teal-50 tw-transition-all tw-duration-300">
                    {path.icon}
                  </div>
                  <h3 className="fw-semibold tw-text-slate-900 mb-2" style={{ fontSize: '1rem' }}>
                    {path.title}
                  </h3>
                  <p className="tw-text-slate-600 mb-3" style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                    {path.desc}
                  </p>
                  <a
                    href={path.link}
                    className="mt-auto fw-semibold tw-text-sipl-navy hover:tw-text-sipl-orange text-decoration-none d-inline-flex align-items-center gap-1 tw-transition-colors tw-duration-300"
                    style={{ fontSize: '0.85rem' }}
                  >
                    {path.contactLabel}
                    <span className="d-inline-block group-hover:tw-translate-x-1 tw-transition-transform tw-duration-300">
                      &rarr;
                    </span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          5. FINAL LIGHTWEIGHT CTA
          ───────────────────────────────────────────────────────── */}
      <section className="py-5 tw-bg-sipl-navy tw-text-white text-center position-relative overflow-hidden">
        <div className="container py-2 position-relative z-1 sipl-reveal">
          <h2 className="fw-bold text-white mb-3" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
            Ready to build something exceptional?
          </h2>
          <p className="tw-text-slate-300 mb-4 mx-auto" style={{ maxWidth: '540px', fontSize: '0.975rem' }}>
            Submit your requirement above or email us directly. We're ready to partner with you on your digital transformation journey.
          </p>
          <a
            href="#contact-section"
            className="btn fw-bold px-4 py-3 tw-bg-sipl-orange tw-text-white hover:tw-bg-orange-600 hover:tw-scale-105 active:tw-scale-98 tw-transition-all tw-duration-300 shadow-sm"
            style={{ borderRadius: '8px' }}
          >
            Start Your Enquiry
          </a>
        </div>
      </section>

    </div>
  );
};

export default Contact;
